import { KeyValueObservable } from './key-value-observable.model'
import { Observable } from 'rxjs'
import { Injectable } from '@angular/core'
import { Data } from './data'

/**
 * @author yaqub
 */
@Injectable({ providedIn: 'root' })
export class CacheLocalStorageTimestampService {
	private cacheList: KeyValueObservable[] = []

	private get storage(): Storage | null {
		return typeof localStorage === 'undefined' ? null : localStorage
	}

	// I initialize the localStorage service.
	constructor() {
		// SSR guard: localStorage is not available on the server.
		if (typeof localStorage === 'undefined') return
		// load all local storage and set into cache array
		for (let i = 0; i < (this.storage?.length ?? 0); i++) {
			const keyValue: KeyValueObservable = new KeyValueObservable()

			const key = this.storage?.key(i)
			if (key === null || key === undefined || key === '') continue

			const item = this.storage?.getItem(key)
			if (item === null || item === undefined || item === '') continue

			if (!this.isValidJSON(item)) {
				continue
			}

			const data: Data = JSON.parse(item)

			keyValue.key = key
			keyValue.source.next(data.value)
			keyValue.timestamp = data.timestamp
			keyValue.hours = data.hours

			this.cacheList.push(keyValue)
		}
	}

	// remove just value of item
	public removeItemValue(key: string): void {
		for (let i = 0; i < this.cacheList.length; i++) {
			if (this.cacheList[i].key === key) {
				this.cacheList[i].source.next(null)

				// localStorage data change
				const data: Data = new Data()
				data.value = null
				data.timestamp = this.cacheList[i].timestamp
				data.hours = this.cacheList[i].hours
				this.storage?.setItem(key, JSON.stringify(data))

				break
			}
		}
	}

	// remove just value of item
	// get key to remove items content keyWords
	public removeItemValueWithKeyNoTage(key: string): void {
		for (let i = 0; i < this.cacheList.length; i++) {
			if (this.cacheList[i].key.startsWith(key)) {
				this.cacheList[i].source.next(null)

				// localStorage data change
				const data: Data = new Data()
				data.value = null
				data.timestamp = this.cacheList[i].timestamp
				data.hours = this.cacheList[i].hours
				this.storage?.setItem(this.cacheList[i].key, JSON.stringify(data))
			}
		}
	}

	// update expire time of data
	public updateTimestamp(key: string, hours: number): void {
		for (let i = 0; i < this.cacheList.length; i++) {
			if (this.cacheList[i].key === key) {
				this.cacheList[i].timestamp = new Date().getTime()
				this.cacheList[i].hours = hours

				const item = this.storage?.getItem(key)
				if (item === null || item === undefined) return

				// localStorage data change
				let data: Data = new Data()
				data = JSON.parse(item)
				data.timestamp = this.cacheList[i].timestamp
				data.hours = this.cacheList[i].hours
				this.storage?.setItem(key, JSON.stringify(data))

				break
			}
		}
	}

	// remove item and that  Observable source
	public removeCompletelyItem(key: string): void {
		for (let i = 0; i < this.cacheList.length; i++) {
			if (this.cacheList[i].key === key) {
				this.cacheList.splice(i, 1)
				break
			}
		}
		this.storage?.removeItem(key)
	}

	// remove item and that  Observable source
	public removeCompletelyItemWithKeyNoTage(key: string): void {
		for (let i = 0; i < this.cacheList.length; i++) {
			if (this.cacheList[i].key.startsWith(key)) {
				this.storage?.removeItem(this.cacheList[i].key)
				this.cacheList.splice(i, 1)
				i--
			}
		}
	}

	public isItem(key: string): boolean {
		for (const cache of this.cacheList) {
			if (cache.key === key) {
				return true
			}
		}
		return false
	}

	public isItemAndNotExpired(key: string): boolean {
		for (const cache of this.cacheList) {
			if (cache.key === key) {
				if (cache.getHours() > new Date().getTime()) {
					return true
				}
			}
		}
		return false
	}

	// I return the localStorage item with the given key.
	public getItem(key: string): Observable<any> {
		for (const cache of this.cacheList) {
			if (cache.key === key) {
				return cache.source
			}
		}

		// if item not found create new item and return that
		this.setItem(key, null)
		return this.getItem(key)
	}

	// I return the in-memory item with the given key.
	public getItemNotExpired(key: string): Observable<any> {
		for (const cache of this.cacheList) {
			if (cache.key === key) {
				if (cache.getHours() > new Date().getTime()) {
					return cache.source
				}
			}
		}

		this.setItem(key, null)
		return this.getItemNotExpired(key)
	}

	// I store the item at the given key. with hours
	public setItemTimed(key: string, value: any, hours: number): void {
		const data: Data = new Data()
		data.value = value
		data.hours = hours
		data.timestamp = new Date().getTime()

		let found = false

		for (let i = 0; i < this.cacheList.length; i++) {
			if (this.cacheList[i].key === key) {
				this.cacheList[i].source.next(data.value)
				this.cacheList[i].timestamp = data.timestamp
				this.cacheList[i].hours = data.hours
				found = true
				break
			}
		}

		if (!found) {
			const keyValue: KeyValueObservable = new KeyValueObservable()
			keyValue.key = key
			keyValue.source.next(data.value)
			keyValue.timestamp = data.timestamp
			keyValue.hours = data.hours
			this.cacheList.push(keyValue)
		}

		const storage = this.storage

		if (storage === null) return

		storage.setItem(key, JSON.stringify(data))
	}

	// I store the item at the given key. infinite time
	public setItem(key: string, value: any): void {
		this.setItemTimed(key, value, 999999)
	}

	private isValidJSON(jsonString: string) {
		try {
			JSON.parse(jsonString)
			return true
		} catch (error) {
			return false
		}
	}
}
